import { lazy, Suspense } from "react";
import { Header } from "@/components/Header";
import { Hero } from "@/components/Hero";
import { BestSellers } from "@/components/BestSellers";
import { Footer } from "@/components/Footer";
import { SEOHead, createHomepageSchema, createFAQSchema } from "@/components/SEOHead";

import { FAQ } from "@/components/FAQ";
import { HomepageAuthorityHub } from "@/components/HomepageAuthorityHub";
import { TrustBarLogos } from "@/components/TrustBarLogos";
import { HomepageSEOHub } from "@/components/HomepageSEOHub";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";

// Lazy load non-critical components
const NewProductsSection = lazy(() =>
  import("@/components/NewProductsSection").then((m) => ({ default: m.NewProductsSection })),
);
const AllProductsPreview = lazy(() =>
  import("@/components/AllProductsPreview").then((m) => ({ default: m.AllProductsPreview })),
);

const InternalLinks = lazy(() => import("@/components/InternalLinks").then((m) => ({ default: m.InternalLinks })));

// Minimal loading fallback
const SectionFallback = () => <div className="py-12" />;

// Homepage structured data for Google Rich Results
const homepageSchema = createHomepageSchema();

// FAQ Schema for People Also Ask - COMMERCIAL questions only (no informational)
// Informational questions like "vad är thca", "är thca lagligt" belong on blogs
const homepageFAQs = [
  {
    question: "Hur snabbt levererar ni?",
    answer:
      "Beställningar före kl 14:00 skickas samma dag. Leveranstid är 1-3 vardagar med PostNord. Alla paket skickas diskret utan synlig märkning.",
  },
  {
    question: "Är det fri frakt?",
    answer: "Ja, fri frakt på alla beställningar över 499 kr till hela Sverige.",
  },
  {
    question: "Vilken styrka ska jag välja?",
    answer:
      "Magic Sauce har 99% renhet och passar de flesta. Om du är ny till THCA kan Snozy (80% renhet) passa bättre för en mildare upplevelse.",
  },
  {
    question: "Hur länge håller en vape?",
    answer: "En Magic Sauce THCA vape ger cirka 1000 puffar. Vid normal användning håller den 2-4 veckor.",
  },
  {
    question: "Vilka betalningsmetoder accepterar ni?",
    answer: "Vi accepterar Visa, Mastercard och Revolut. Alla betalningar är krypterade och säkra.",
  },
  {
    question: "Kan jag spåra min beställning?",
    answer: "Ja, du får ett spårningsnummer via e-post när paketet skickas. Följ leveransen på PostNords hemsida.",
  },
];
const faqSchema = createFAQSchema(homepageFAQs);

// Default SEO values
const DEFAULT_SEO = {
  title: "THCA Sverige – Köp THCA Vape & Magic Sauce Lagligt",
  description:
    "THCA Sverige – köp THCA vape och Magic Sauce vape lagligt. Hög kvalitet, naturliga cannabinoider och snabb leverans direkt till dig.",
};

/**
 * Homepage with 11 sections:
 * 1. Hero (H1: THCA Sverige – Köp THCA Vape och Magic Sauce Lagligt)
 * 2. BestSellers (THCA Vape Sverige)
 * 3. HomepageAuthorityHub (Product overview)
 * 4. NewProductsSection (Nyheter)
 * 5. AllProductsPreview (THCA Buds & Hash Sverige)
 * 6. Categories
 * 7. TrustBarLogos (Real brand logos)
 * 8. HomepageSEOHub (NEW - 900+ word authority content)
 * 9. FAQ
 * 10. InternalLinks
 */
const Index = () => {
  useRevealOnScroll();

  const seoTitle = DEFAULT_SEO.title;
  const seoDescription = DEFAULT_SEO.description;

  return (
    <>
      <SEOHead
        title={seoTitle}
        description={seoDescription}
        canonical="/"
        keywords="thca sverige, thca vape, magic sauce vape, köp thca, thca hash, thca buds, magic sauce, vapebolag"
        schema={[...homepageSchema, faqSchema]}
      />
      <div className="min-h-screen bg-background">
        <Header />
        <main className="space-y-0">
          {/* 1. Hero */}
          <Hero />

          {/* 2. Trust Bar */}
          <TrustBarLogos />

          {/* 3. BestSellers */}
          <div className="reveal-section">
            <BestSellers />
          </div>

          {/* 4. New Products */}
          <div className="reveal-section">
            <Suspense fallback={<SectionFallback />}>
              <NewProductsSection />
            </Suspense>
          </div>

          {/* 5. THCA Buds & Hash */}
          <div className="reveal-section">
            <Suspense fallback={<SectionFallback />}>
              <AllProductsPreview />
            </Suspense>
          </div>

          {/* 6. Authority Hub */}
          <div className="reveal-section">
            <HomepageAuthorityHub />
          </div>

          {/* 7. SEO Content */}
          <div className="reveal-section">
            <HomepageSEOHub />
          </div>

          {/* 8. FAQ */}
          <div className="reveal-section">
            <FAQ />
          </div>

          {/* 9. Internal Links */}
          <div className="reveal-section">
            <Suspense fallback={<SectionFallback />}>
              <InternalLinks variant="homepage" />
            </Suspense>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
};

export default Index;
